import { Dayjs } from 'dayjs';
import { CaseSubType } from '../enums/CaseSubType.enum';
import { ChiroUpJSON } from '../functions/ChiroUpJSON';
import { ComplaintSummary, ComplaintSummaryType } from './ComplaintSummary.type';
import { ClaimResponseViewType } from './Era.type';
import { IntegrationInvoice } from './Invoice.type';
import { Modalities } from './Modalities.type';
import { PatientSurvey } from './PatientSurvey.type';
import { PatientTransaction, PatientTransactionItemType } from './PatientTransaction.type';
import { PayorLineItemType } from './Payor.type';
import { EncounterReExamInfo } from './ReExam.type';
import { ReferenceCodeDisplay } from './ReferenceCode.type';
import { BodyParts, SourceOfPain, SurveyQuestion } from './Survey.type';
import { TestOrder } from './Test.type';
import { SoapNoteDetailsPreferences } from './User.type';
import { Vital } from './Vital.type';

export type VisitTimes = {
  id?: number;
  times: number;
  timesUnit: 'day' | 'week' | 'month';
  duration: number;
  durationUnit: 'day' | 'week' | 'month';
  createdAt?: number;
  carePlanId?: number;
};

/** The CarePlan columns a new encounter carries forward from the prior same-track encounter. */
export type CarePlanRow = {
  id: number;
  carePlanId: number | null;
  visitNumber: number | null;
  nextReEval: number | null;
  goal: number | null;
  notes: string | null;
  totalVisits?: number | null;
};

export type EncounterInfo = {
  id?: number;
  carePlanId?: number;
  visitNumber?: number;
  nextReEval?: number;
  createdBy?: string;
  createdAt?: number;
  updatedBy?: string;
  updatedAt?: number;
  visits?: VisitTimes[];
  totalVisits?: number;
  complaints?: CarePlanComplaint[];
  communications?: string[];
  visitComplaintSurveyId?: string;
  plan?: string;
  goal?: number;
  tests?: TestOrder[];
  otherSurveys?: PatientSurvey[];
  patientId?: string;
  clinicId?: number;
  active?: boolean;
  nextVisitType?: string;
  encounters?: {
    id: string;
    visitStatus: string;
    visitDate: string;
  }[];
  soapNoteDetailPreference?: SoapNoteDetailsPreferences;
  /** Legacy inline claim info. Flag-on clinics hold NONE here — the migration
   *  strips it and resolution happens at read time (record-level Claim Info). */
  claimInfo?: ClaimInfo;
  visitTimes?: VisitTimes[];
  /** Sentinel set when the user explicitly clears the visit schedule via the X button.
   *  Lets merge logic distinguish an intentional clear from a stale empty Yjs snapshot. */
  visitTimesClearedByUser?: boolean;
  notes?: string;
  resetCarePlan?: boolean;
  nextRequestedEncounterDate?: string | number | null;
  surveysToDocument?: { [key: string]: boolean };
  detachedSurveys?: DetachedSurveyMeta[];
};

export type DetachedSurveyMeta = {
  patientSurveyId: string;
  surveyId: string;
  detachedAt: number;
  detachedForEncounterId: string;
  detachedFromTrackId?: number;
  completedAt?: number;
  appointmentGroupId?: number;
  prId?: string;
  surveyType?: ComplaintSummaryType;
};

export type Complaint = {
  id: string;
  patientId: string;
  clinicId: number;
  status: ComplaintStatuses;
  survey: PatientSurvey;
  origin?: ComplaintOrigins;
  inactiveReason?: string;
  surveyId?: string;
  complaint?: string;
  region?: string;
  bodypicker?: {
    parts: BodyParts;
    sourceOfPain: SourceOfPain;
  };
  /** true when bodypicker was derived from surveys during visit fetch */
  bodypickerDerived?: boolean;
  surveyQuestions?: { survey: { questions: SurveyQuestion[] } };
  problemArea?: number;
  questions?: {
    questions: SurveyQuestion[];
    english: { questions: SurveyQuestion[] };
  };
  createdAt: number;
  updatedAt: number;
  added?: number;
  prn: boolean;
};

export type ConditionReferenceCodeDisplay = ReferenceCodeDisplay & {
  conditionId: number;
  name?: string; // Condition name (stored in V2 for SOAP generation without lookup)
  // primary: boolean;
  order?: number; // Can sort by this at some point, maybe?
  referenceCodes?: ReferenceCodeDisplay[];
  essentialCondition: boolean;
};

export type CarePlanComplaint = {
  id?: string;
  origin?: ComplaintOrigins;
  surveyId?: string;
  complaint?: string;
  skipped?: boolean;
  region?: string;
  subjective?: string;
  objective?: string;
  assessment?: {
    nonConditions?: { referenceCodes: ReferenceCodeDisplay[] };
    conditions?: ConditionReferenceCodeDisplay[];
    goal?: number;
    text?: string;
    value?: ImprovementOptions | null;
    note?: string;
    /** V2 collaborative encounters historically stored prn here; canonical location is complaint.prn */
    prn?: boolean;
  };
  bodypicker?: {
    parts: BodyParts;
    sourceOfPain: SourceOfPain;
  };
  /** true when bodypicker was derived from surveys during visit fetch */
  bodypickerDerived?: boolean;
  surveyQuestions?: { survey: { questions: SurveyQuestion[] } };
  problemArea?: number;
  questions?: {
    questions: SurveyQuestion[];
    english: { questions: SurveyQuestion[] };
  };
  added: number;
  createdAt?: number;
  status: ComplaintStatuses;
  survey?: PatientSurvey;
  inactiveReason?: string;
  deactivatedReferredProviderId?: number;
  prn: boolean;
  createNewComplaint?: boolean;
};

export type VisitModality = {
  visitId: string;
  modalityId: number;
};

export type ModalityHistory = {
  ID: number;
  completedAt?: Date | string;
  completedBy?: string;
  completedByName?: string;
  heat: boolean;
  ice: boolean;
  intensity: number;
  name?: string;
  region: string[];
  time: number;
  visitId: string;
};

export enum ComplaintStatuses {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PAUSED = 'paused',
  DELETED = 'deleted',
}

export enum ComplaintInactiveReasons {
  RESOLVED = 'resolved',
  REFERRED = 'referred',
  OTHER = 'other',
}

export const enum ComplaintOrigins {
  CCS = 'ccs',
  Reeval = 'reeval',
}

export const enum SOAPTemplates {
  Blank = 'Blank SOAP',
  Wizard = 'SOAP Wizard',
}

export type OfficeExerciseHistory = {
  id: number;
  patientId: string;
  carePlanId: number | null;
  visitId: string;
  completedBy: string;
  completedAt: string;
  clinicianName?: string;
  exerciseNames?: string;
  // User who reassigned the completed-by attribution; null/undefined = original completion
  changedBy?: string | null;
  changedByName?: string | null;
  // Attributed user's signature S3 key frozen at write time
  completedBySignature?: string | null;
};

// Latest in-office completion attribution, frozen into Encounter.detail for
// signed-note rendering (regenerated by generateVisitDetail).
export type ExercisesCompletion = {
  completedBy: string;
  completedByName?: string | null;
  completedAt: string;
  completedBySignature?: string | null;
};
export type DetailObject = {
  notes?: string;
  type?: string | string[];
  region?: string;
};

export type VisitPlanKeyType = {
  [key: number | string]: {
    units?: number;
    services?: string[];
    unitServices?: any;
    detailNotes?: string;
    detail?: DetailObject[];
    testType?: TestTypes;
  };
};

export enum TestTypes {
  InHouse = 'inHouse',
  OutOfHouse = 'outOfHouse',
}

export type VisitPlanType = {
  [key: string]: any; // Get out of jail free card.
  evaluationAndManagement?: VisitPlanKeyType;
  modalities?: VisitPlanKeyType;
  supplementsAndSupplies?: VisitPlanKeyType;
  imagingAndTests?: VisitPlanKeyType;
  treatments?: TreatmentObject;
  rehab?: VisitPlanKeyType;
  courtesyBilling?: boolean;
};

export type VisitDetail = {
  tests?: {
    id: number;
    notes: string;
    sides: string;
    views: number;
    region: string;
    facility: number;
    testName: string;
    facilityName: string;
  }[];
  /** Tracks deleted test IDs for V2 real-time sync across clients */
  deletedTestIds?: number[];
  vitals?: Vital;
  exercises?: {
    currentExercises?: {
      ID: number;
      name: string;
      order: number;
      phase: number;
      prescription?: string | null;
      prescriptionNotes?: string | null;
    }[];
    inOfficeExercises?: {
      ID: number;
      name: string;
      order: number;
      phase: number;
      prescription?: string | null;
      prescriptionNotes?: string | null;
    }[];
  };
  // Sibling of `exercises` on purpose: exerciseUpdate.ts replaces
  // detail.exercises wholesale on every exercise save, which would drop a key
  // nested inside it.
  exercisesCompletion?: ExercisesCompletion | null;
  modalities?: Partial<Modalities>[];
  communications?: {
    exerciseReports: any[];
    conditionReports: any[];
  };
};

// export interface FlexibleCCSSection {
//   type: 'section';
//   title: string;
//   items: Array<string | FlexibleCCSItem>;
// }

// export interface FlexibleCCSResponse {
//   complaintId: string;
//   problem: string;
//   sections: FlexibleCCSSection[];
// }

export type Recording = {
  id: number;
  encounterId?: string;
  key: string;
  createdAt: number;
  transcription?: string;
  /** Encounter load carries this flag instead of the transcript text. */
  hasTranscription?: boolean;
  description: string;
  status?: 'completed' | 'failed' | 'filtered' | 'duration-exceeded' | 'cron-error';
  error?: string;
};

export enum NoteAIState {
  None = 'None',
  Generating = 'Generating',
  Generated = 'Generated',
  Filtered = 'Filtered',
  Error = 'Error',
}

export type InProgressComplaintSurvey = {
  patientRequestId: string;
  patientSurveyId: string;
  surveyId: string;
  name: string;
  /** Defaults to true in older API responses that predate this field. */
  started?: boolean;
};

/** GET encounter with surveyStatusOnly=true returns this read-only projection. */
export type EncounterSurveyStatus = {
  revision: string;
};

export type SurveyStatusChangedMessage = {
  type: 'surveyStatusChanged';
  communicationId?: string;
};

export type PatientSurveyStatusChange = {
  clinicId: number;
  patientId: string;
  previousSurvey?: PatientSurvey;
  survey?: PatientSurvey;
  communicationId?: string;
};

export type EncounterSurveyReadOptions = {
  clinicId: number;
  patientId: string;
  surveyCommunicationIds?: string[];
};

export type UsePatientVisitOptions = {
  refreshSurveyStatus?: boolean;
};

export type Visit = {
  id?: string;
  /** Opaque revision of survey availability, start, and completion state. */
  surveyStatusRevision?: string;
  transactionId?: number;
  createdAt?: Dayjs | number;
  updatedAt?: Dayjs | number;
  clinician?: {
    id?: string;
    name?: string;
    degree?: string;
  };
  exercisesComplete?: boolean;
  encounterInfo?: EncounterInfo;
  note?: { [key: string]: SOANote } | null;
  ccsSurveyId?: string | null;
  visitDate?: number | string | null | Dayjs;
  patientId?: string;
  clinicId?: number;
  locationId?: number;
  carePlanId?: number;
  visitNumber?: number;
  carePlanTotalVisits?: number;
  interimSurveyId?: string;
  interimSurvey?: PatientSurvey;
  reevaluationSurvey?: PatientSurvey;
  reevaluationSurveyId?: string;
  reevaluationSurveyIds?: string[];
  additionalComplaintSurveyIds?: string[];
  soapTemplate?: SOAPTemplates | null;
  notes?: blankSOAPNOTE | null;
  signed?: string | null;
  signedAt?: number | null;
  signedByUserId?: string | null; // UUID who clicked Sign; NULL only on pre-feature rows
  signatureDetails?: EncounterSignatureDetails | null;
  delegateUserIds?: string[]; // any clinic UUID allowed to sign note on behalf of provider
  supervisorDelegateUserIds?: string[]; // same, on behalf of the supervising provider
  fullSignatureLink?: string;
  visitStatus?: VisitStatus;
  testsComplete?: boolean;
  modalitiesComplete?: boolean;
  modalitiesCompletedAt?: Dayjs | number | null;
  modalitiesLastUpdated?: Dayjs | number;
  vitals?: Partial<Vital>;
  vitalsComplete?: boolean;
  prId?: string;
  lastVisitObjectiveData?: { [key: string]: ObjectiveNote };
  lastVisitUnifiedObjective?: ObjectiveNote;
  plan?: VisitPlanType | null;
  surveys?: any;
  allSurveys?: any;
  inProgressComplaintSurveys?: InProgressComplaintSurvey[];
  services?: PatientTransactionItemType[];
  importDiagnosesAlways?: boolean;
  mostRecentEncounterNumber?: number;
  tz?: string;
  externalPrintReasons?: string | null;
  hasAppointment?: boolean;
  insuranceSubmitted?: string;
  lifetimeVisits?: number;
  caseSubtype?: CaseSubType;
  appointmentGroupId?: number;
  soapCustomEditWarning?: {
    subjective?: boolean;
    objective?: boolean;
    assessment?: boolean;
    plan?: boolean;
  };
  detail?: VisitDetail;
  supervisingProvider?: {
    id?: string;
    name?: string;
    degree?: string;
  } | null;
  /** Contracting provider for this visit. Stored on PatientTransaction
   *  (the Encounter table has no such column) and joined in by getEncounter,
   *  same as caseSubtype and treatmentName. */
  contractingProviderId?: string | null;
  recordings?: Recording[];
  trackId?: number | null;
  trackSendsSurveys?: boolean;
  trackName?: string;
  /** Re-exam alert info (story 7707378836), attached by getFullEncounter. */
  reExam?: EncounterReExamInfo;
  /** Immutable snapshot stamped at creation: were Tracks off for the patient when
   *  this encounter was created? Governs whether the track is shown for the encounter
   *  (the patient's live tracksOff governs editability). */
  createdWithTracksOff?: boolean;
  createTrackCCSSubmission?: boolean;
  regenerateFromTrackChange?: boolean;
  clinicianName?: string;
  planWizardCategory?: PlanWizardCategory[];
  version?: number | null; // v2 for new unified objective format, null for legacy per-complaint format
  unifiedObjective?: ObjectiveNote & {
    carryForward?: boolean;
    activeComplaintRegions?: string[];
    deactivatedComplaintRegions?: string[];
  }; // v2 unified objective data
  hasSubsequentEncounters?: boolean;
  complaintSummaries?: { [key: string]: ComplaintSummary };
  complaintsToSurveys?: {
    [complaintId: string]: {
      surveyType: 'ccs' | 'interim' | 'reevaluation' | 'abbrevCCS' | 'additionalComplaint';
      surveyId: string;
    };
  };
  treatmentId?: number;
  treatmentName?: string;
};

export type PlanWizardSelection = {
  ID: number | string;
  name: string;
  hideInPlan?: boolean;
  includeCptInOutput?: boolean;
  favorite?: boolean;
  primaryCptCode?: string;
  secondaryCptCode?: string;
  maxUnits?: number;
  isSystemDefault?: boolean;
  output?: string;
  categoryId?: number;
  baseId: number | string;
  subCategory?: string;
  order?: number;
  /**
   * Plan selection service codes — the import-and-point contract.
   *
   * A selection has EITHER `services` (single primary CPT, all `units` go to
   * it) OR `unitServices` (split: primary + secondary). NEVER both.
   *
   * `unitServices` keys: `'__'` is always present; `'1'` is present only
   * when a primary code exists (the producer omits it otherwise):
   *   - `'1'`  → the primary code, used for unit slot 1
   *   - `'__'` → the secondary code, fills every slot beyond 1 (or all
   *              slots when `'1'` is absent)
   * Example: `{ '1': ['97810'], '__': ['97811'] }` at units=3 → 97810 × 1,
   * 97811 × 2.
   *
   * Type-enforced at the wizard UI (discriminated unions):
   *   - apps/chiroup/.../PlanWizardTreatmentOptions.tsx
   *   - apps/chiroup/.../PlanWizardCategoriesContainer.tsx
   * Sole producer:
   *   - libs/server-core/src/lib/functions/visitHelpers.ts
   *     (`getPlanWizardCategoryPreferences`, ~L841)
   *
   * Import semantics (bug 11992205440):
   *   - Same-code contributions sum across sources/categories (no cap).
   *   - E&M uses key-walk; Modalities/Supplements/Imaging use slot-walk —
   *     different code shapes, identical results under this contract.
   *
   * If you find data that breaks any of the above (extra keys, both fields
   * set, etc.), DO NOT just patch the consumer — the producer or a writer
   * is wrong.
   */
  services?: string[];
  unitServices?: { [key: string]: string[] };
};

export type PlanWizardCategory = {
  id: string | number;
  name: string;
  visible: boolean;
  collapsed: boolean;
  autoSalt: boolean;
  order?: number;
  selections?: PlanWizardSelection[];
};

export type TreatmentObject = {
  [key: string]: {
    [key: string]: {
      units?: number;
      detail?: { [key: string]: any };
      detailNotes?: string;
      services?: string[];
      unitServices?: any;
    };
  };
};

export enum VisitStatus {
  Completed = 'completed',
  InProgress = 'inProgress',
  NotStarted = 'notStarted',
}

export enum ImprovementOptions {
  SignificantlyWorse = 'SignificantlyWorse',
  Worse = 'Worse',
  Unchanged = 'Unchanged',
  Improved = 'Improved',
  SignificantlyImproved = 'SignificantlyImproved',
}

export enum ImprovementOptionsText {
  SignificantlyWorse = 'Significantly Worse',
  Worse = 'Worse',
  Unchanged = 'Unchanged',
  Improved = 'Improved',
  SignificantlyImproved = 'Significantly Improved',
}

export enum ObjectiveParts {
  RangeOfMotion = 'RangeOfMotion',
  Joints = 'Joints',
  Myofascial = 'Myofascial',
  Orthopedic = 'Orthopedic',
  Neurological = 'Neurological',
}

/**
 * Range of Motion body regions used for cleaning touched flags
 */
enum RangeOfMotionRegions {
  Cervical = 'C',
  Thoracic = 'T',
  Lumbar = 'L',
  Extremity = 'E',
  FootAnkle = 'F/A',
  HandWrist = 'H/W',
  Hip = 'H',
  Knee = 'K',
  Shoulder = 'S',
}

/**
 * Array of all ObjectiveParts for iteration
 */
export const OBJECTIVE_PARTS_ARRAY = [
  ObjectiveParts.Joints,
  ObjectiveParts.RangeOfMotion,
  ObjectiveParts.Orthopedic,
  ObjectiveParts.Neurological,
  ObjectiveParts.Myofascial,
] as const;

/**
 * Array of all RangeOfMotion regions for iteration
 */
export const RANGE_OF_MOTION_REGIONS_ARRAY = [
  RangeOfMotionRegions.Cervical,
  RangeOfMotionRegions.Thoracic,
  RangeOfMotionRegions.Lumbar,
  RangeOfMotionRegions.Extremity,
  RangeOfMotionRegions.FootAnkle,
  RangeOfMotionRegions.HandWrist,
  RangeOfMotionRegions.Hip,
  RangeOfMotionRegions.Knee,
  RangeOfMotionRegions.Shoulder,
] as const;

type ObjectiveNoteDetails = {
  [key in ObjectiveParts]?: {
    value?: ImprovementOptions;
    detail?: any;
    side?: string;
    touched?: boolean;
    showGlobal?: boolean;
  };
};
export type ObjectiveNote = ObjectiveNoteDetails & {
  extraNotes?: string;
  deactivatedComplaintRegions?: string[];
};

export type SOANote = {
  subjectiveAI?: { type: 'doc'; content: any[] } | string;
  subjective?: { type: 'doc'; content: any[] } | string;
  objective?: ObjectiveNote;
  assessment?: string;
  plan?: string;
  conditions?: number[];
  complaintId?: number | string;
  subjectiveCarryForward?: boolean;
  objectiveCarryForward?: boolean;
  assessmentCarryForward?: boolean;
  additionalSections?: { [key: string]: string }[];
};

export type blankSOAPNOTE = {
  subjective?: string | { type: 'doc'; content: any[] };
  objective?: string;
  assessment?: string;
  plan?: string;
  subjectiveAI?: { type: 'doc'; content: any[] } | string | null;
  objectiveAI?: { type: 'doc'; content: any[] } | string;
  assessmentAI?: { type: 'doc'; content: any[] } | string;
  planAI?: { type: 'doc'; content: any[] } | string;
  signatureKey?: string;
  additionalSections?: { [key: string]: string }[];
  generatedAI?: NoteAIState;
  subjectiveAIEditedAt?: number;
};

export type ClaimInfo = {
  nature?: ClaimNature;
  onsetDate?: string | number;
  // relatedEvent?: ClaimRelatedEvent;
  // relatedEventDate?: string | number;
  disabilityStartDate?: string | number;
  disabilityEndDate?: string | number;
  unableWorkStartDate?: string | number;
  unableWorkEndDate?: string | number;
  hospitalStartDate?: string | number;
  hospitalEndDate?: string | number;
  providerType?: ProviderType;
  providerId?: string | number;
  providerName?: string;
  providerNpi?: string;
  idNumberType?: IdNumberType;
  idNumber?: string;
  employment?: boolean;
  auto?: boolean;
  autoState?: string;
  otherAccident?: boolean;
  accidentDate?: string | number;
  acuteManifestationChronicConditionDate?: string | number;
  firstVisitDate?: string | number;
  initialTreatmentDate?: string | number;
  lastXRayDate?: string | number;
  latestVisitDate?: string | number;
  prescriptionDate?: string | number;
  reportStartDate?: string | number;
  reportEndDate?: string | number;
  EPSDTRefferal?: EPSDTRefferalType;
};

/** Widget-bar view of a `PatientClaimInfo` profile row. */
export type ClaimInfoProfile = {
  /** Display label — joined track names, or the unassigned placeholder. */
  providerName: string;
  claimInfo: ClaimInfo;
  /** `PatientClaimInfo.id` of the profile row. */
  profileId?: string;
  trackNames?: string[];
  active?: boolean;
  showOnWidgetBar?: boolean;
  unassigned?: boolean;
  /**
   * Legacy (flag-OFF) feed — `encounterClaimInfoProfilesGet` derives the
   * profile from the newest encounter with claim info. `encounterId` is the
   * edit target when `profileId` is absent.
   */
  encounterId?: string;
  providerId?: string | null;
  visitDate?: number;
};

export type TransactionClaimsWriteOffInfo = {
  /** Optional when nested in a parent object that already has these fields */
  transactionDate?: string | number;
  billingKey?: string;
  clinicId?: number;
  canWriteOff: boolean;
  canAllocate: boolean;
  totalBilledAmount: number;
  patientServiceAmount: number;
  patientPostedPaymentAmount: number;
  claimWriteOffTotal: number;
  /** Sum of existing PatientWriteOff items on the transaction */
  patientInvoiceWriteOffTotal?: number;
  amountAllocatedToPatient: number;
  waitingOnAdjudication: boolean;

  finalPatientResponsibility: number;
  finalAdjustment: number;
  finalPayorPaidAmount: number;
  finalRemainingUnallocatedBalance: number;
  canWriteOffFinal: boolean;
  canAllocateFinal: boolean;

  /** Single source of truth: suggested write-off amount to apply for the patient on this post */
  suggestedPatientWriteOffAmount?: number;

  currentClaimPayorName: string;
  otherPostedPayorPaymentAmount: number;

  payorLineItems?: PayorLineItemType[];

  /**
   * [2025-08-21.1644 by Brian] These were added to stop some REST and
   * database call madness. Three REST calls were all fired off almost
   * simultaneously, all asking for the same data. So we took 2-3x
   * database hits, REST hits, user waiting,...
   *
   * The concept of 'usage' was added to where this was got and these
   * extra fields come back with the claim info when its the right
   * usage.
   */
  transaction?: PatientTransaction;
  claimResponse?: ClaimResponseViewType;
  invoice?: IntegrationInvoice;

  /**
   * [2025-08-26.0919 by Brian] The UI will use these for determining
   * whether a message is shown or the user must add a write-off.
   */
  isPostingPrimary?: boolean;
  isSeniorPosted?: boolean;
  isJuniorPostedOrApplied?: boolean;
  isLastInvoice?: boolean;
  showPrimaryNotPostedWarningFlag?: boolean;
  showManualWriteOffWarningFlag?: boolean;
  isTakeback?: boolean;
};

export enum EPSDTRefferalType {
  Yes = 'yes',
  No = 'no',
  AvailableNotUsed = 'available',
  UnderTreatment = 'underTreatment',
  NewServiceRequested = 'newService',
  NotUsed = 'notUsed',
  NotApplicable = 'notApplicable',
}

export const EdiIncludeEPSDTRefferal = {
  [EPSDTRefferalType.Yes]: false,
  [EPSDTRefferalType.No]: true,
  [EPSDTRefferalType.AvailableNotUsed]: true,
  [EPSDTRefferalType.UnderTreatment]: true,
  [EPSDTRefferalType.NewServiceRequested]: true,
  [EPSDTRefferalType.NotUsed]: true,
  [EPSDTRefferalType.NotApplicable]: false,
};

export enum ClaimNature {
  AcuteCondition = 'acute',
  ChronicCondition = 'chronic',
  NonAcute = 'nonAcute',
  NonLifeThreatening = 'nonLifeThreatening',
  Routine = 'routine',
  Symptomatic = 'symptomatic',
  AcuteManifestation = 'acuteManifestation',
}

export const ClaimNatureEdiXref = {
  [ClaimNature.AcuteCondition]: 'A',
  [ClaimNature.ChronicCondition]: 'C',
  [ClaimNature.NonAcute]: 'D',
  [ClaimNature.NonLifeThreatening]: 'E',
  [ClaimNature.Routine]: 'F',
  [ClaimNature.Symptomatic]: 'G',
  [ClaimNature.AcuteManifestation]: 'M',
};

export enum ClaimRelatedEvent {
  InitialTreatment = '454',
  Latest = '304',
  Acute = '453',
  Accident = '439',
  XRay = '455',
  Prescription = '471',
  ReportStart = '090',
  ReportEnd = '091',
  FirstVisit = '444',
}

export const ClaimNatureSelectOptions: {
  text: string;
  value: ClaimNature;
}[] = [
  {
    text: 'Acute Condition',
    value: ClaimNature.AcuteCondition,
  },
  {
    text: 'Chronic Condition',
    value: ClaimNature.ChronicCondition,
  },
  { text: 'Non-acute', value: ClaimNature.NonAcute },
  {
    text: 'Non-life threatening',
    value: ClaimNature.NonLifeThreatening,
  },
  { text: 'Routine', value: ClaimNature.Routine },
  { text: 'Symptomatic', value: ClaimNature.Symptomatic },
  {
    text: 'Acute Manifestation of a Chronic Condition',
    value: ClaimNature.AcuteManifestation,
  },
];

/**
 * TODO: Yeah, this is weird. No "billing" provider. No way
 * to add more than one on a claim.
 */
export enum ProviderType {
  ReferringProvider = 'referingProvider',
  OrderingProvider = 'orderingProvider',
  SupervisingProvider = 'supervisingProvider',
}

export enum IdNumberType {
  StateLicenseNumber = 'state',
  UPINNumber = 'upin',
  ProviderCommercialNumber = 'commercial',
}

export type VisitSnapshot = {
  id: string;
  patientId: string;
  patientName: string;
  encounterDate: Dayjs | string | number;
  signedAt: Dayjs | string | number | null;
  signingStatus: 'signed' | 'unsigned';
  clinicianName?: string;
  treatmentType?: string;
  caseSubtype?: string;
  plan?: VisitPlanType;
  detail?: VisitDetail;
  tz?: string; // Timezone from clinic location
  complaints: Array<{
    name: string;
    status: ComplaintStatuses;
    vasScore?: number | null;
    vasScoreHigh?: number | null;
    reason?: string;
    origin?: string;
    diagnoses?: string[];
    descriptors?: string[];
    skipped?: boolean;
    inactiveReason?: string;
  }>;
};

/**
 * Structured representation of an encounter's signature details.
 *
 * The Encounter table stores the signer's S3 key in `signed` and the
 * timestamp in `signedAt`. This type enriches that raw data with the
 * signer's identity so consumers don't have to parse the S3 key or
 * assume the clinician is the signer.
 */
export class EncounterSignatureDetails {
  /** S3 key of the signature image (e.g. "providers/signatures/{userId}/{uuid}.png") */
  signature: string;
  /** Epoch ms when the note was signed */
  timestamp: number;
  /** User ID of the person who signed (extracted from the S3 key) */
  userId: string;
  /** Display name of the signer (e.g. "Dr. Ryan Woods") */
  userDisplayName: string;
  /** Degree of the signer (e.g. "DC") */
  userDegree: string;

  static UNKNOWN = '- unknown -';

  constructor(params: {
    signature: string;
    timestamp: number;
    userId: string;
    userDisplayName: string;
    userDegree: string;
  }) {
    this.signature = params.signature;
    this.timestamp = params.timestamp;
    this.userId = params.userId;
    this.userDisplayName = params.userDisplayName;
    this.userDegree = params.userDegree;
  }

  /** Formatted name + degree for display next to the signature (e.g. "Dr. Ryan Woods DC") */
  displayName(): string {
    return [this.userDisplayName, this.userDegree].filter(Boolean).join(' ');
  }

  /**
   * Hydrate from JSON (string or plain object) into a class instance.
   * Returns `null` if the input is falsy or missing required fields.
   */
  static fromJSON(json: unknown): EncounterSignatureDetails | null {
    if (json instanceof EncounterSignatureDetails) return json;

    const data = ChiroUpJSON.parse(json as any);

    if (!data?.signature || !data?.userId) return null;

    return new EncounterSignatureDetails({
      signature: data.signature,
      timestamp: data.timestamp ?? 0,
      userId: data.userId,
      userDisplayName: data.userDisplayName ?? EncounterSignatureDetails.UNKNOWN,
      userDegree: data.userDegree ?? '',
    });
  }
}

/**
 * Extract the signer's user ID from a `signed` S3 key.
 *
 * The key format is: `providers/signatures/{userId}/{uuid}.png`
 */
export const extractSignerUserId = (signed: string): string | null => {
  const match = signed.match(/providers\/signatures\/([^/]+)\//);

  return match?.[1] ?? null;
};
